import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EventCard from "./components/EventCard";
import { hotEvents, siteCategories } from "@/mocks/events";

type SortKey = "heat" | "topic" | "repost" | "latest";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "heat", label: "热度最高" },
  { key: "topic", label: "话题量最多" },
  { key: "repost", label: "转发量最多" },
  { key: "latest", label: "最新首爆" },
];

const EVENTS_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "事件追踪库 · 网红黑曝传播追踪",
    description:
      "收录正在发酵的网红黑曝事件，支持按商业争议、带货翻车、人设崩塌、情感纠纷与网红言论分类检索，并对比热度与话题量。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://gualian.example.com/" },
      { "@type": "ListItem", position: 2, name: "事件追踪库", item: "https://gualian.example.com/events" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "网红黑曝事件列表",
    itemListElement: hotEvents.map((event, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: event.title,
      url: `https://gualian.example.com/event/${event.id}`,
    })),
  },
];

export default function Events() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "全部";
  const keyword = searchParams.get("q") || "";
  const [sortKey, setSortKey] = useState<SortKey>("heat");

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === "全部") params.delete("category");
      else params.set("category", next.category);
    }
    if (next.q !== undefined) {
      if (!next.q.trim()) params.delete("q");
      else params.set("q", next.q);
    }
    setSearchParams(params, { replace: true });
  };

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const filtered = hotEvents.filter((event) => {
      const matchCategory = activeCategory === "全部" || event.category === activeCategory;
      const matchKeyword =
        !kw ||
        event.title.toLowerCase().includes(kw) ||
        event.summary.toLowerCase().includes(kw) ||
        event.category.toLowerCase().includes(kw) ||
        event.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });

    return filtered.sort((a, b) => {
      if (sortKey === "topic") return b.topicCount - a.topicCount;
      if (sortKey === "repost") return b.repost - a.repost;
      if (sortKey === "latest") return b.startDate.localeCompare(a.startDate);
      return b.heat - a.heat;
    });
  }, [activeCategory, keyword, sortKey]);

  const totalTopic = list.reduce((sum, event) => sum + event.topicCount, 0);

  return (
    <>
      <Seo
        title="事件追踪库 - 网红黑曝事件热度与话题量对比 | 瓜链"
        description="瓜链事件追踪库收录正在发酵的网红黑曝事件，可按商业争议、带货翻车、人设崩塌、情感纠纷与网红言论分类筛选，并按热度值、话题量、转发量排序对比传播规模。"
        keywords="网红黑曝事件,网红黑曝话题量,黑曝事件列表,网红黑曝上热搜,传播追踪库"
        canonicalPath="/events"
        jsonLd={EVENTS_JSONLD}
      />
      <div className="min-h-screen bg-background-50">
        <SiteHeader />

        <section className="bg-background-100 border-b border-background-200 pt-28 md:pt-32 pb-10 md:pb-12">
          <div className="w-full px-4 md:px-8">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "事件追踪库" }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-4xl font-black text-foreground-950 tracking-tight">
              事件追踪库
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              这里收录正在发酵的网红黑曝事件。每个条目都记录了首爆平台、首爆时间、当前热度与话题量，
              你可以按传播类型筛选，再用排序对比同一类事件的热度走势差异。
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <span className="text-foreground-600">
                当前筛选 <strong className="font-label text-foreground-950">{list.length}</strong> 个事件
              </span>
              <span className="text-foreground-600">
                合计话题量 <strong className="font-label text-foreground-950">{totalTopic}万</strong>
              </span>
              <span className="text-foreground-600">
                数据口径 <strong className="font-label text-foreground-950">按小时观测热度</strong>
              </span>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-8 md:py-10">
          <div className="w-full px-4 md:px-8">
            <div className="rounded-2xl bg-background-100 border border-background-200 p-4 md:p-5">
              <div className="flex flex-col xl:flex-row xl:items-center gap-4">
                <div className="flex items-center gap-2 flex-wrap flex-1">
                  {siteCategories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => updateParams({ category })}
                      className={`px-3.5 py-1.5 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                        activeCategory === category
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-50 border border-background-300 text-foreground-700 hover:text-foreground-950 hover:border-primary-300"
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="flex items-center gap-2 bg-background-50 border border-background-300 rounded-md px-3 h-10 sm:w-60 focus-within:border-primary-400 transition-colors">
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-search-line text-base" />
                    </span>
                    <input
                      value={keyword}
                      onChange={(e) => updateParams({ q: e.target.value })}
                      placeholder="搜索事件、标签或类型"
                      className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
                      aria-label="搜索事件"
                    />
                    {keyword && (
                      <button
                        type="button"
                        onClick={() => updateParams({ q: "" })}
                        className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
                        aria-label="清空搜索"
                      >
                        <i className="ri-close-line" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-2 bg-background-50 border border-background-300 rounded-md px-3 h-10">
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-sort-desc" />
                    </span>
                    <select
                      value={sortKey}
                      onChange={(e) => setSortKey(e.target.value as SortKey)}
                      className="bg-transparent outline-none text-sm text-foreground-900 cursor-pointer pr-1"
                      aria-label="排序方式"
                    >
                      {sortOptions.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {list.length > 0 ? (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {list.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <div className="mt-10 rounded-2xl border border-background-200 bg-background-100 py-16 px-6 text-center">
                <span className="mx-auto w-14 h-14 rounded-2xl bg-background-200 text-foreground-500 flex items-center justify-center">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有找到匹配的事件
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  试着换一个关键词，或者把分类切换回「全部」。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSearchParams(new URLSearchParams(), { replace: true });
                  }}
                  className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label whitespace-nowrap transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-refresh-line" />
                  </span>
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </section>

        <SiteFooter />
        <BackToTop />
      </div>
    </>
  );
}